/**
 * SegmentFilterGroup - Grupo de filtros unidos por AND
 * 
 * Permite adicionar múltiplos filtros que devem ser todos satisfeitos.
 * 
 * @author Sistema de Campanhas
 * @version 1.0.0
 */

import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SegmentFilterRow } from './SegmentFilterRow';
import {
  SegmentFilterGroup as FilterGroupType,
  SegmentFilter,
  createEmptyFilter,
} from '@/types/campaign-segments';
import { CampaignSegmentOptionsResult } from '@/hooks/useCampaignSegmentOptions';

interface SegmentFilterGroupProps {
  group: FilterGroupType;
  groupIndex: number;
  onChange: (group: FilterGroupType) => void;
  onRemove: () => void;
  options: CampaignSegmentOptionsResult;
  crmEnabled: boolean;
  isOnlyGroup?: boolean;  // Se é o único grupo (não pode remover)
}

/**
 * Componente de grupo de filtros (AND)
 */
export function SegmentFilterGroup({
  group,
  groupIndex,
  onChange,
  onRemove,
  options,
  crmEnabled,
  isOnlyGroup = false,
}: SegmentFilterGroupProps) {
  /**
   * Adiciona um novo filtro ao grupo
   */
  const handleAddFilter = () => {
    onChange({
      ...group,
      filters: [...group.filters, createEmptyFilter()],
    });
  };

  /**
   * Atualiza um filtro específico
   */
  const handleFilterChange = (index: number, filter: SegmentFilter) => {
    const newFilters = [...group.filters];
    newFilters[index] = filter;
    onChange({ ...group, filters: newFilters });
  };

  /**
   * Remove um filtro específico
   */
  const handleFilterRemove = (index: number) => {
    if (group.filters.length <= 1) return; // Manter pelo menos 1 filtro
    
    onChange({
      ...group,
      filters: group.filters.filter((_, i) => i !== index),
    });
  };

  return (
    <Card className="border-2 border-dashed">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs font-bold">
              Grupo {groupIndex + 1}
            </span>
            <span className="text-muted-foreground font-normal">
              Todos os filtros devem ser satisfeitos (AND)
            </span>
          </CardTitle>
          
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddFilter}
              className="gap-1"
            >
              <Plus className="h-3 w-3" />
              Adicionar filtro
            </Button>
            
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onRemove}
              disabled={isOnlyGroup}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-2 pb-4">
        {group.filters.map((filter, index) => (
          <div key={filter.id}>
            {/* Indicador AND entre filtros */}
            {index > 0 && (
              <div className="flex items-center gap-2 py-1 ml-4">
                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  E
                </span>
                <div className="flex-1 border-t border-dashed" />
              </div>
            )}
            
            <SegmentFilterRow
              filter={filter}
              onChange={(f) => handleFilterChange(index, f)}
              onRemove={() => handleFilterRemove(index)}
              options={options}
              crmEnabled={crmEnabled}
              isOnlyFilter={group.filters.length === 1}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
